import axios from 'axios'

import {
    appConfigContext,
    IAppServicesConfigState,
    TRestrictedRegionMap,
} from 'nexchange-service/appServiceConfig'
import { ECoinTypeAvailabilityMode } from 'nexchange-service/module/coins'
import { intercomConfigContext } from 'intercom-service'

import { timeConstant } from '@/src/constants'
import {
    regenerateToken,
    removeAuthenticationClientSide,
    saveAuthentication,
} from 'nexchange-service'
import { parseCookies } from 'nookies'

type TExchangeWidgetConfig = {
    REF_NAME: string // for debug purposes
    FROM_AVAILABILITY_MODE: ECoinTypeAvailabilityMode
    TO_AVAILABILITY_MODE: ECoinTypeAvailabilityMode
}

const DOMAIN_NAME = process.env.DOMAIN // set in next.config.ts

axios.defaults.baseURL = ''

const config = {
    IS_DEVELOPMENT: process.env.IS_DEVELOPMENT,
    NODE_ENV: process.env.NODE_ENV,
    VERCEL_ENV: process.env.VERCEL_ENV,

    DOMAIN: process.env.NODE_ENV === 'production' ? 'https://easywallet.io' : DOMAIN_NAME,
    NEXT_API_URL: `/api`,
    API_URL: process.env.NEXT_PUBLIC_API_URL || 'https://api.n.exchange/en/api/v1',

    PARTNERSHIP_EMAIL: 'partners@n.exchange',
    SUPPORT_EMAIL: 'support@easywallet.io',
    SUPPORT_PHONE: '+44 116 318 3915',

    PRICE_FETCH_INTERVAL: 60000,
    COIN_PRICES_INTERVAL: 60000,
    ORDER_DETAILS_INTERVAL: 10000,
    RECENT_ORDERS_SIZE: 5,
    RECENT_ORDERS_INTERVAL: 30000,

    WARNING_TIMEOUT: 5000,
    COIN_INPUT_DEBOUNCE: 500,
    DEFAULT_COIN_MIN_AMOUNT_MULTIPLIER: 2,
    DEFAULT_COIN_MIN_AMOUNT_CUSTOMS: [250, 500, 1000],
    CUSTOM_EXCLUDED_COINS: ['ZEC', 'XMR', 'DASH'],
    CUSTOM_POPULAR_COINS: ['BTC', 'ETH', 'USDTERC', 'USDC', 'LTC', 'ADA', 'BNBBSC', 'XRP'],
    EXCHANGE_WIDGET_CONFIGS: {
        INDEX: <TExchangeWidgetConfig>{
            REF_NAME: '',
            FROM_AVAILABILITY_MODE: ECoinTypeAvailabilityMode.FIAT as ECoinTypeAvailabilityMode,
            TO_AVAILABILITY_MODE: ECoinTypeAvailabilityMode.CRYPTO as ECoinTypeAvailabilityMode,
        },
        IFRAME: {
            DEFAULT: <TExchangeWidgetConfig>{
                REF_NAME: 'default',
                FROM_AVAILABILITY_MODE: ECoinTypeAvailabilityMode.FIAT as ECoinTypeAvailabilityMode,
                TO_AVAILABILITY_MODE: ECoinTypeAvailabilityMode.CRYPTO as ECoinTypeAvailabilityMode,
            },
        },
    },

    GOOGLE_TAG_MANAGER_CONTAINER_ID: 'GTM-KHQS3XX',
    REFERRAL_CODE: null,

    AUTH_EXPIRATION_PERIOD: 8 * timeConstant.WEEK,

    DEFAULT_PAYMENT_METHOD: 'checkout',
    DEFAULT_KYC_PROVIDER: 'sumsub',

    SUM_SUB: {
        API_URL: 'https://api.sumsub.com',
        // API_URL: 'https://test-api.sumsub.com',
        ACCESS_TOKEN_LIFESPAN_IN_SECONDS: 600,
        DEFAULT_KYC_LEVEL: 'basic-kyc-level',
        FLOW_NAME: {
            TIER_1: 'Kyc for Tier 1',
        },
    },

    IDENFY: {
        API_URL: 'https://ivs.idenfy.com/api/v2',
        KEY: process.env.IDENFY_KEY,
        SECRET: process.env.IDENFY_SECRET,
    },

    TEST_ORDERS: {
        TEST_WALLET_ADDRESSES: [
            'DLzYk9yAc6ByYL3GCTzgDcBTaxGbrKgRr5',
            'rQngsXfArxKiQ7swxmgMkGPKutFfeKWZvW', // XRP
        ],
        TEST_COIN_CODES: ['XVG'],
    },

    RESTRICTED_REGION_MAP: <TRestrictedRegionMap>{
        RU: { countryName: 'Russia' },
        BY: { countryName: 'Belarus' },
        IS: { countryName: 'Iceland' },
        IL: { countryName: 'Israel' },
        MA: { countryName: 'Morocco' },
        DZ: { countryName: 'Algeria' },
        EC: { countryName: 'Ecuador' },
        BO: { countryName: 'Bolivia' },
        MK: { countryName: 'Macedonia' },
        EG: { countryName: 'Egypt' },
        SA: { countryName: 'Saudi Arabia' },
        IQ: { countryName: 'Iraq' },
        PK: { countryName: 'Pakistan' },
        NP: { countryName: 'Nepal' },
        BD: { countryName: 'Bangladesh' },
        CN: { countryName: 'China' },
        KG: { countryName: 'Kyrgyzstan' },
        BH: { countryName: 'Bahrain' },
        NA: { countryName: 'Namibia' },
        QA: { countryName: 'Qatar' },
        AF: { countryName: 'Afghanistan' },
        KP: { countryName: 'North Korea' },
        SY: { countryName: 'Syria' },
        IR: { countryName: 'Iran' },
        CU: { countryName: 'Cuba' },
        US: { countryName: 'USA' },
        UM: { countryName: 'USA Overseas' },
        GB: { countryName: 'United Kingdom' },
        UA: {
            countryName: 'Ukraine',
            prohibitedStates: [
                { isoCode: '43', name: 'Crimea region of Ukraine' },
                { isoCode: '14', name: 'Donetsk region of Ukraine' },
                { isoCode: '9', name: 'Luhansk region of Ukraine' },
            ],
        },
        AL: { countryName: 'Albania' },
        BS: { countryName: 'The Bahamas' },
        BB: { countryName: 'Barbados' },
        BW: { countryName: 'Botswana' },
        GH: { countryName: 'Ghana' },
        JM: { countryName: 'Jamaica' },
        PA: { countryName: 'Panama' },
        UG: { countryName: 'Uganda' },
        YE: { countryName: 'Yemen' },
        ZW: { countryName: 'Zimbabwe' },
        MM: { countryName: 'Myanmar' },
        MU: { countryName: 'Mauritius' },
        KH: { countryName: 'Cambodia' },
        MN: { countryName: 'Mongolia' },
    },
    SHORT_RESTRICTED_REGION_MAP: <TRestrictedRegionMap>{
        US: { countryName: 'USA' },
        GB: { countryName: 'United Kingdom' },
    },
    PARTNER_WITH_REFUND_STEP: ['BC-EN', 'BC-RU', 'BC-EW-EN', 'BC-EW-RU'],
}

const constants = {
    ROUTES: {
        HOME: '/',
        LEGAL: {
            COOKIES: '/legal/cookies',
            PRIVACY: '/legal/privacy',
            TERMS: '/legal/terms',
        },
    },
}

appConfigContext.setAppConfig(config as unknown as IAppServicesConfigState)
intercomConfigContext.setConfig({ INTERCOM_APP_ID: 'a3zrft9d' })

export default { ...config, ...constants }
